import React, { useState, useEffect } from 'react';
import axios from 'axios';
import UserLayout from '../../components/user/UserLayout';
import toast from 'react-hot-toast';
import { ArrowDownToLine, Clock, CheckCircle, XCircle, DollarSign, Save, Edit2 } from 'lucide-react';

export default function WithdrawalPage() {
  const [balance, setBalance] = useState(0);
  const [withdrawals, setWithdrawals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savedDetails, setSavedDetails] = useState(null);
  const [useQuickConfirm, setUseQuickConfirm] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [form, setForm] = useState({
    amount: '', method: 'bank',
    bankName: '', accountNumber: '', ifscCode: '',
    accountHolderName: '', upiId: '',
    cryptoAddress: '', cryptoNetwork: 'TRC20',
    saveDetails: true
  });

  const [withdrawalEnabled, setWithdrawalEnabled] = useState(true);
  const [minAmount, setMinAmount] = useState(100);
  const [paymentConfig, setPaymentConfig] = useState({});

  const fetchData = async () => {
    const [bal, wList, savedRes, cfgRes] = await Promise.all([
      axios.get('/api/wallet/balance'),
      axios.get('/api/withdrawal/mine'),
      axios.get('/api/withdrawal/saved-details').catch(() => ({ data: { withdrawalDetails: null } })),
      axios.get('/api/payment/admin-payment-config').catch(() => ({ data: {} }))
    ]);
    const cfg = cfgRes.data;
    setWithdrawalEnabled(cfg.withdrawalEnabled !== false);
    setMinAmount(cfg.minWithdrawalAmount || 100);
    setPaymentConfig(cfg);
    setBalance(bal.data.commissionWallet || 0);
    setWithdrawals(wList.data.withdrawals);
    const details = savedRes.data.withdrawalDetails;
    if (details?.method) {
      setSavedDetails(details);
      setUseQuickConfirm(true);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleOpenForm = () => {
    if (savedDetails?.method && !editingDetails) {
      // Pre-fill with saved details
      setForm(f => ({
        ...f,
        method: savedDetails.method || 'bank',
        bankName: savedDetails.bankName || '',
        accountNumber: savedDetails.accountNumber || '',
        ifscCode: savedDetails.ifscCode || '',
        accountHolderName: savedDetails.accountHolderName || '',
        upiId: savedDetails.upiId || '',
        cryptoAddress: savedDetails.cryptoAddress || '',
        cryptoNetwork: savedDetails.cryptoNetwork || 'TRC20'
      }));
    }
    setOtpSent(false);
    setOtpCode('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amountValue = parseFloat(form.amount);
    if (!otpSent) {
      if (!Number.isFinite(amountValue)) return toast.error('Please enter a valid withdrawal amount.');
      if (amountValue > balance) return toast.error('Insufficient commission wallet balance.');
      if (amountValue < minAmount) return toast.error(`Minimum withdrawal is $${minAmount}`);
      if (!form.method) return toast.error('Please select a withdrawal method.');
      if (form.method === 'bank' && (!form.bankName || !form.accountNumber || !form.ifscCode || !form.accountHolderName)) {
        return toast.error('Please fill all bank details before requesting withdrawal.');
      }
      if (form.method === 'upi' && !form.upiId) {
        return toast.error('Please enter your UPI ID.');
      }
      if (form.method === 'crypto' && !form.cryptoAddress) {
        return toast.error('Please enter your crypto wallet address.');
      }
    }
    setLoading(true);
    try {
      if (!otpSent) {
        await axios.post('/api/withdrawal/request', { ...form, amount: amountValue });
        toast.success('OTP sent to your email. Enter it below to confirm withdrawal.');
        setOtpSent(true);
      } else {
        if (!otpCode) return toast.error('Please enter the OTP sent to your email.');
        await axios.post('/api/withdrawal/request', {
          otp: otpCode.trim(),
          amount: amountValue,
          method: form.method,
          bankName: form.bankName,
          accountNumber: form.accountNumber,
          ifscCode: form.ifscCode,
          accountHolderName: form.accountHolderName,
          upiId: form.upiId,
          cryptoAddress: form.cryptoAddress,
          cryptoNetwork: form.cryptoNetwork,
        });
        toast.success('Withdrawal request submitted successfully!');
        setShowForm(false);
        setEditingDetails(false);
        setForm(f => ({ ...f, amount: '' }));
        setOtpSent(false);
        setOtpCode('');
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDetails = async () => {
    try {
      await axios.put('/api/withdrawal/save-details', {
        method: form.method,
        accountHolderName: form.accountHolderName,
        bankName: form.bankName,
        accountNumber: form.accountNumber,
        ifscCode: form.ifscCode,
        upiId: form.upiId,
        cryptoAddress: form.cryptoAddress,
        cryptoNetwork: form.cryptoNetwork
      });
      toast.success('Withdrawal details updated!');
      setEditingDetails(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to update details');
    }
  };

  const statusIcon = {
    pending: <Clock className="w-4 h-4 text-amber-400" />,
    approved: <CheckCircle className="w-4 h-4 text-primary-400" />,
    rejected: <XCircle className="w-4 h-4 text-red-400" />
  };
  const statusBadge = {
    pending: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    approved: 'bg-primary-500/20 text-primary-300 border-primary-500/30',
    rejected: 'bg-red-500/20 text-red-300 border-red-500/30'
  };

  const inputClass = "w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500";

  return (
    <UserLayout>
      <div className="animate-fade-in">
        <h1 className="text-2xl font-bold text-white mb-2">Withdrawal</h1>
        <p className="text-gray-400 mb-6">Withdraw your commission earnings</p>

        <div className="bg-gradient-to-br from-primary-600/20 to-green-600/10 border border-primary-500/30 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm mb-1">Available Commission Balance</p>
              <p className="text-4xl font-bold text-white">${balance.toFixed(2)}</p>
              <p className="text-gray-500 text-xs mt-1">Minimum withdrawal: ${minAmount}</p>
            </div>
            <div className="w-16 h-16 bg-primary-500/20 rounded-2xl flex items-center justify-center">
              <DollarSign className="w-8 h-8 text-primary-400" />
            </div>
          </div>
          {!withdrawalEnabled && (
            <div className="mt-3 bg-red-500/20 border border-red-500/30 rounded-xl p-3 flex items-center gap-2">
              <span className="text-red-400 text-sm font-semibold">⚠️ Withdrawals are currently disabled by admin.</span>
            </div>
          )}
          <button
            onClick={handleOpenForm}
            disabled={balance < minAmount || !withdrawalEnabled}
            className="mt-4 bg-primary-600 hover:bg-primary-500 disabled:bg-dark-600 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-semibold px-6 py-2.5 rounded-xl transition-colors flex items-center gap-2">
            <ArrowDownToLine className="w-4 h-4" />
            {balance < minAmount ? `Insufficient Balance` : 'Request Withdrawal'}
          </button>
        </div>

        {/* Change 8: Saved details card */}
        {savedDetails?.method && !showForm && (
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold">Saved Payment Details</h3>
              <button onClick={() => { setEditingDetails(true); handleOpenForm(); }}
                className="flex items-center gap-1 text-primary-400 hover:text-primary-300 text-sm">
                <Edit2 className="w-3.5 h-3.5" /> Edit
              </button>
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Method</span>
                <span className="text-white capitalize">{savedDetails.method}</span>
              </div>
              {savedDetails.accountHolderName && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Name</span>
                  <span className="text-white">{savedDetails.accountHolderName}</span>
                </div>
              )}
              {savedDetails.bankName && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Bank</span>
                  <span className="text-white">{savedDetails.bankName}</span>
                </div>
              )}
              {savedDetails.accountNumber && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Account</span>
                  <span className="text-white font-mono">****{savedDetails.accountNumber.slice(-4)}</span>
                </div>
              )}
              {savedDetails.upiId && (
                <div className="flex justify-between">
                  <span className="text-gray-400">UPI ID</span>
                  <span className="text-white">{savedDetails.upiId}</span>
                </div>
              )}
              {savedDetails.cryptoNetwork && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Network</span>
                  <span className="text-white">{savedDetails.cryptoNetwork}</span>
                </div>
              )}
              {savedDetails.cryptoAddress && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Wallet</span>
                  <span className="text-white font-mono text-xs truncate">...{savedDetails.cryptoAddress.slice(-10)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Withdrawal Form */}
        {showForm && (
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6 mb-6 animate-slide-up">
            <h3 className="text-white font-semibold mb-5">
              {otpSent ? 'Verify Withdrawal OTP' : savedDetails?.method && !editingDetails ? 'Confirm Withdrawal' : 'Withdrawal Details'}
            </h3>
            {otpSent && (
              <div className="bg-primary-500/10 border border-primary-500/20 rounded-xl p-4 mb-4 text-sm text-primary-100">
                OTP has been sent to your registered email address. Enter it below to confirm the withdrawal request.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Amount ($)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input type="number" value={form.amount}
                    onChange={e => setForm({ ...form, amount: e.target.value })}
                    className="w-full bg-dark-600 border border-white/10 text-white rounded-xl pl-8 pr-4 py-3 focus:outline-none focus:border-primary-500"
                    placeholder={`Max: $${balance.toFixed(2)}`} min={minAmount} max={balance} required disabled={otpSent} />
                </div>
                <div className="flex gap-2 mt-2">
                  {[500, 1000, 2000, Math.floor(balance)].filter((v, i, a) => v <= balance && a.indexOf(v) === i).map(a => (
                    <button key={a} type="button" onClick={() => setForm({ ...form, amount: String(a) })}
                      className="px-3 py-1 bg-dark-600 border border-white/10 text-gray-300 hover:text-white rounded-lg text-xs transition-colors"
                      disabled={otpSent}>
                      ${a}
                    </button>
                  ))}
                </div>
              </div>

              {otpSent && (
                <div className="bg-dark-600 border border-primary-500/20 rounded-xl p-4 text-sm text-gray-300">
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Enter OTP</label>
                  <input type="text" value={otpCode} onChange={e => setOtpCode(e.target.value)}
                    className={inputClass} placeholder="123456" maxLength={6} />
                </div>
              )}

              {/* Change 8: Quick confirm if saved details exist and not editing */}
              {savedDetails?.method && !editingDetails ? (
                <div className="bg-primary-500/10 border border-primary-500/20 rounded-xl p-4">
                  <p className="text-primary-300 text-sm font-medium mb-2">Using saved payment details:</p>
                  <div className="text-sm space-y-1">
                    {savedDetails.method === 'bank' && (
                      <>
                        <p className="text-gray-300">{savedDetails.accountHolderName} • {savedDetails.bankName}</p>
                        {savedDetails.accountNumber && <p className="text-gray-400 font-mono">****{savedDetails.accountNumber.slice(-4)}</p>}
                      </>
                    )}
                    {savedDetails.method === 'upi' && (
                      <p className="text-gray-300">{savedDetails.accountHolderName} • {savedDetails.upiId}</p>
                    )}
                    {savedDetails.method === 'crypto' && (
                      <>
                        <p className="text-gray-300">{savedDetails.cryptoNetwork} Network</p>
                        <p className="text-gray-400 font-mono text-xs truncate">...{savedDetails.cryptoAddress?.slice(-15)}</p>
                      </>
                    )}
                  </div>
                  <button type="button" onClick={() => setEditingDetails(true)}
                    className="mt-2 text-primary-400 hover:text-primary-300 text-xs underline">
                    Use different details
                  </button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">Payment Method</label>
                    <div className="flex gap-2">
                      {['bank', 'upi', 'crypto'].map(m => (
                        <button key={m} type="button" onClick={() => setForm(f => ({ ...f, method: m }))}
                          className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all capitalize ${form.method === m ? 'border-primary-500 bg-primary-500/20 text-white' : 'border-white/10 bg-dark-600 text-gray-400'}`}>
                          {m === 'bank' ? 'Bank Account' : m === 'upi' ? 'UPI' : 'Crypto'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">Account Holder Name</label>
                    <input type="text" value={form.accountHolderName} onChange={e => setForm({ ...form, accountHolderName: e.target.value })}
                      className={inputClass} placeholder="As per bank records" required />
                  </div>

                  {form.method === 'bank' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1.5">Bank Name</label>
                        <input type="text" value={form.bankName} onChange={e => setForm({ ...form, bankName: e.target.value })}
                          className={inputClass} placeholder="e.g. HDFC Bank" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1.5">Account Number</label>
                        <input type="text" value={form.accountNumber} onChange={e => setForm({ ...form, accountNumber: e.target.value })}
                          className={`${inputClass} font-mono`} placeholder="Enter account number" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1.5">IFSC Code</label>
                        <input type="text" value={form.ifscCode} onChange={e => setForm({ ...form, ifscCode: e.target.value.toUpperCase() })}
                          className={`${inputClass} uppercase font-mono`} placeholder="e.g. HDFC0001234" required />
                      </div>
                    </>
                  )}

                  {form.method === 'upi' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">UPI ID</label>
                      <input type="text" value={form.upiId} onChange={e => setForm({ ...form, upiId: e.target.value })}
                        className={inputClass} placeholder="yourname@upi" required />
                    </div>
                  )}

                  {form.method === 'crypto' && (
                    <div className="md:col-span-2 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1.5">Crypto Network</label>
                        <select value={form.cryptoNetwork} onChange={e => setForm({ ...form, cryptoNetwork: e.target.value })}
                          className={inputClass}>
                          <option value="TRC20">TRON (TRC20)</option>
                          <option value="BEP20">BSC (BEP20)</option>
                          <option value="ERC20">Ethereum (ERC20)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1.5">Crypto Wallet Address</label>
                        <input type="text" value={form.cryptoAddress} onChange={e => setForm({ ...form, cryptoAddress: e.target.value })}
                          className={`${inputClass} font-mono`} placeholder="0x... or T..." required />
                      </div>
                    </div>
                  )}

                  <div className="md:col-span-2 flex items-center gap-3">
                    <input type="checkbox" id="saveDetails" checked={form.saveDetails}
                      onChange={e => setForm({ ...form, saveDetails: e.target.checked })}
                      className="w-4 h-4 rounded border-white/20 bg-dark-600 text-primary-500" />
                    <label htmlFor="saveDetails" className="text-gray-300 text-sm flex items-center gap-1.5 cursor-pointer">
                      <Save className="w-3.5 h-3.5" /> Save these details for future withdrawals
                    </label>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button type="button" onClick={() => { setShowForm(false); setEditingDetails(false); setOtpSent(false); setOtpCode(''); }}
                  className="flex-1 bg-dark-600 border border-white/10 text-gray-300 hover:text-white font-semibold py-3 rounded-xl transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={loading}
                  className="flex-1 bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    : <><ArrowDownToLine className="w-4 h-4" /> {otpSent ? 'Confirm OTP' : savedDetails?.method && !editingDetails ? 'Confirm Withdrawal' : 'Submit Request'}</>}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Withdrawal History */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-white/5">
            <h3 className="text-white font-semibold">Withdrawal History</h3>
          </div>
          <div className="divide-y divide-white/5">
            {withdrawals.length === 0 ? (
              <div className="py-12 text-center">
                <ArrowDownToLine className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">No withdrawal requests yet</p>
              </div>
            ) : withdrawals.map(w => (
              <div key={w._id} className="px-4 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-dark-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      {statusIcon[w.status]}
                    </div>
                    <div>
                      <p className="text-white font-semibold">${w.amount.toFixed(2)}</p>
                      <p className="text-gray-500 text-xs">Method: {w.method}</p>
                      <p className="text-gray-500 text-xs capitalize">Wallet: {w.walletType || 'commission'}</p>
                      {w.method === 'bank' && <p className="text-gray-500 text-xs">{w.bankName} • {w.accountNumber}</p>}
                      {w.method === 'upi' && w.upiId && <p className="text-gray-500 text-xs">UPI: {w.upiId}</p>}
                      {w.method === 'crypto' && w.cryptoAddress && <p className="text-gray-500 text-xs">Address: {w.cryptoAddress} ({w.cryptoNetwork})</p>}
                      <p className="text-gray-500 text-xs">{new Date(w.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${statusBadge[w.status]}`}>
                    {w.status}
                  </span>
                </div>
                {w.adminNote && <p className="text-gray-500 text-xs mt-2 ml-12">Note: {w.adminNote}</p>}
                {w.transactionId && <p className="text-green-400 text-xs mt-2 ml-12">Transaction ID: {w.transactionId}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </UserLayout>
  );
}
